import { RepeatIcon } from "@chakra-ui/icons";
import { Text, Icon, HStack, Flex, VStack, Tooltip } from "@chakra-ui/react";
import { WalletImportMethod } from "@syla/shared/types/models/WalletBase";
import { formatDistanceStrict } from "date-fns";
import React from "react";
import { MdOutlineFileUpload } from "react-icons/md";
import { formatDateLocal } from "../../../../../helpers/formatDateLocal";

export type WalletDetailsProps = {
  importMethod: WalletImportMethod;
  importMethodName: string;
  createdAt?: Date;
  lastUpdated?: Date;
  addresses?: string[];
  allowSync?: boolean;
  syncState?: string;
  syncStateColor?: string;
};

export const WalletDetails = (
  props: WalletDetailsProps,
): React.ReactElement => {
  const data: {
    label: string;
    value: React.ReactElement | string;
  }[] = [];

  const icon =
    props.importMethod === WalletImportMethod.API ?
      <RepeatIcon boxSize={4} color="green.500" />
    : <Icon as={MdOutlineFileUpload} boxSize={4} color="blue.500" />;

  data.push({
    label: "Type",
    value: (
      <HStack>
        {icon} <Text fontSize="sm">{props.importMethodName}</Text>
      </HStack>
    ),
  });

  const status =
    props.syncState ??
    (props.importMethod === WalletImportMethod.API ? "Connected" : undefined);

  if (status) {
    data.push({
      label: "Status",
      value: (
        <Text fontSize="sm" color={props.syncStateColor ?? undefined}>
          {status}
        </Text>
      ),
    });
  }

  if (props.createdAt) {
    data.push({
      label: "Created",
      value: (
        <Tooltip
          label={formatDateLocal(props.createdAt, {
            dateStyle: "medium",
            timeStyle: "short",
          })}
        >
          <Text fontSize="sm">
            {formatDateLocal(props.createdAt, { dateStyle: "medium" })}
          </Text>
        </Tooltip>
      ),
    });
  }

  if (props.lastUpdated) {
    const distanceText = (
      <Tooltip
        label={formatDateLocal(props.lastUpdated, {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      >
        <Text fontSize="sm">{`${formatDistanceStrict(props.lastUpdated, new Date())} ago`}</Text>
      </Tooltip>
    );

    data.push({
      label: "Last updated",
      value: distanceText,
    });
  }

  if (props.addresses && props.addresses.length === 1) {
    data.push({
      label: "Added",
      value: props.addresses[0],
    });
  }

  return (
    <Flex gap={{ base: 4, xs: 8, lg: 12 }} flexWrap="wrap">
      {data.map((item, index) => (
        <VStack align="flex-start" key={index} spacing={1}>
          <Text fontSize="sm" fontWeight="semibold">
            {item.label}
          </Text>
          {typeof item.value === "string" ?
            <Text fontSize="sm">{item.value}</Text>
          : item.value}
        </VStack>
      ))}
    </Flex>
  );
};
